import React, { useState, useEffect } from 'react';
import { api } from '../api';
import PersonPicker from './PersonPicker';

export const AP_EMPTY = () => ({ _key: Math.random(), name: '', telefon: '', mobil: '', email: '', funktion: '' });

const TYP_OPTIONS = ['Kunde', 'Lieferant', 'Werkstatt', 'Versicherer', 'Leasinggeber', 'Sonstiges'];

// ─── KontaktForm Modal ────────────────────────────────────────────────────────

export default function KontaktForm({ initial, defaultTyp, onSave, onClose }) {
  const initialTypen = initial
    ? (initial.typ || '').split(',').map(t => t.trim()).filter(t => TYP_OPTIONS.includes(t))
    : (defaultTyp ? [defaultTyp] : []);

  const [form, setForm] = useState(initial ? { ...initial } : {
    name: '', name2: '', strasse: '', plz: '', ort: '', land: '',
    telefon: '', mobil: '', email: '', website: '', notizen: ''
  });
  const [typen, setTypen] = useState(initialTypen);
  const [apList, setApList] = useState([]);       // Personen die verknüpft sind/werden
  const [removedApIds, setRemovedApIds] = useState([]);  // zum Trennen beim Speichern
  const [pendingApIds, setPendingApIds] = useState([]);  // neue Verknüpfungen (hat id, aber _pending=true)
  const [apLoading, setApLoading] = useState(!!initial);
  const [showPicker, setShowPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (initial) {
      api.getAnsprechpartner(initial.id)
        .then(aps => setApList(aps.map(ap => ({ ...ap, _key: ap.id }))))
        .catch(() => {})
        .finally(() => setApLoading(false));
    }
  }, [initial]);

  const toggleTyp = (t) => {
    setTypen(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  };

  // Person aus PersonPicker ausgewählt (existierend oder neu erstellt)
  const handlePersonSelected = (person) => {
    setShowPicker(false);
    // Nicht doppelt hinzufügen
    if (apList.some(ap => ap.id === person.id)) return;
    setApList(l => [...l, { ...person, _key: person.id, _pending: true }]);
    setPendingApIds(ids => [...ids, person.id]);
  };

  const updateAP = (idx, k, v) => setApList(l => l.map((ap, i) => i === idx ? { ...ap, [k]: v } : ap));

  const removeAP = (idx) => {
    const ap = apList[idx];
    if (ap.id && !ap._pending) setRemovedApIds(ids => [...ids, ap.id]);
    if (ap._pending) setPendingApIds(ids => ids.filter(id => id !== ap.id));
    setApList(l => l.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name ist erforderlich.'); return; }
    setSaving(true);
    setError('');
    try {
      // eslint-disable-next-line no-unused-vars
      const { ansprechpartner: _ap, ...contactData } = form;
      const typStr = typen.filter(Boolean).join(',') || 'Sonstiges';
      let kontaktId = initial?.id;
      if (initial) {
        await api.updateKontakt(kontaktId, { ...contactData, typ: typStr });
      } else {
        const created = await api.createKontakt({ ...contactData, typ: typStr });
        kontaktId = created.id;
      }

      // Getrennte Personen (unlink)
      for (const id of removedApIds) {
        await api.unlinkAnsprechpartner(kontaktId, id).catch(() => {});
      }

      // Neu verknüpfte Personen aus Picker
      for (const apId of pendingApIds) {
        await api.createAnsprechpartner(kontaktId, { ansprechpartner_id: apId }).catch(() => {});
      }

      // Bestehende Personen aktualisieren (Daten-Änderungen)
      for (const ap of apList) {
        if (ap._pending) continue; // bereits neu angelegt, kein Update nötig
        if (!ap.name?.trim()) continue;
        // eslint-disable-next-line no-unused-vars
        const { _key, _pending, ...apData } = ap;
        if (ap.id) await api.updateAnsprechpartner(ap.id, apData).catch(() => {});
      }

      onSave();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const excludeIds = apList.map(ap => ap.id).filter(Boolean);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 780 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{initial ? 'Kontakt bearbeiten' : 'Neuer Kontakt'}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ maxHeight: 'calc(100vh - 160px)', overflowY: 'auto', padding: 0 }}>
          {error && <div className="alert alert-error" style={{ margin: '16px 24px 0' }}>{error}</div>}
          <form onSubmit={handleSubmit}>

            {/* ── Abschnitt 1: Firmendaten ── */}
            <div style={{ padding: '20px 24px 16px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 14 }}>
                Firmendaten
              </div>
              <div className="form-grid">
                <div className="form-group form-full">
                  <label className="form-label">Name / Firma *</label>
                  <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} autoFocus />
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Zusatz (Marke / Abteilung)</label>
                  <input className="form-input" value={form.name2 || ''} onChange={e => set('name2', e.target.value)} />
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Straße & Hausnummer</label>
                  <input className="form-input" value={form.strasse || ''} onChange={e => set('strasse', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">PLZ</label>
                  <input className="form-input" value={form.plz || ''} onChange={e => set('plz', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Ort</label>
                  <input className="form-input" value={form.ort || ''} onChange={e => set('ort', e.target.value)} />
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Land</label>
                  <input className="form-input" value={form.land || ''} onChange={e => set('land', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Telefon</label>
                  <input className="form-input" type="tel" value={form.telefon || ''} onChange={e => set('telefon', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Mobil</label>
                  <input className="form-input" type="tel" value={form.mobil || ''} onChange={e => set('mobil', e.target.value)} />
                </div>
                <div className="form-group form-full">
                  <label className="form-label">E-Mail</label>
                  <input className="form-input" type="email" value={form.email || ''} onChange={e => set('email', e.target.value)} />
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Website</label>
                  <input className="form-input" value={form.website || ''} onChange={e => set('website', e.target.value)} placeholder="z.B. www.firma.de" />
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Notizen</label>
                  <textarea className="form-textarea" rows={2} value={form.notizen || ''} onChange={e => set('notizen', e.target.value)} />
                </div>
              </div>
            </div>

            {/* ── Abschnitt: Typ ── */}
            <div style={{ borderTop: '2px solid var(--gray-100)', padding: '16px 24px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 12 }}>
                Typ
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {TYP_OPTIONS.map(t => (
                  <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '5px 10px', borderRadius: 6, border: `1px solid ${typen.includes(t) ? 'var(--primary)' : 'var(--gray-200)'}`, background: typen.includes(t) ? 'var(--primary-light)' : '#fff', fontSize: 13, fontWeight: typen.includes(t) ? 600 : 400 }}>
                    <input type="checkbox" checked={typen.includes(t)} onChange={() => toggleTyp(t)} style={{ display: 'none' }} />
                    {t}
                  </label>
                ))}
              </div>
            </div>

            {/* ── Abschnitt: Ansprechpartner ── */}
            <div style={{ borderTop: '2px solid var(--gray-100)', padding: '20px 24px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '.6px' }}>
                  Ansprechpartner
                  {apList.length > 0 && (
                    <span style={{ marginLeft: 8, background: 'var(--gray-200)', color: 'var(--gray-600)', borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 600 }}>
                      {apList.length}
                    </span>
                  )}
                </div>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowPicker(true)}>
                  + Ansprechpartner
                </button>
              </div>

              {apLoading ? (
                <div style={{ fontSize: 12, color: 'var(--gray-400)', padding: '8px 0' }}>Lade…</div>
              ) : apList.length === 0 ? (
                <div style={{
                  border: '2px dashed var(--gray-200)', borderRadius: 10,
                  padding: '24px 20px', textAlign: 'center',
                  color: 'var(--gray-400)', fontSize: 13
                }}>
                  Noch keine Ansprechpartner — suche bestehende Personen oder lege neue an.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {apList.map((ap, idx) => (
                    <div key={ap._key} style={{
                      background: ap._pending ? 'var(--primary-light)' : 'var(--gray-50)',
                      border: `1px solid ${ap._pending ? 'var(--primary)' : 'var(--gray-200)'}`,
                      borderRadius: 10, padding: '14px 16px', position: 'relative'
                    }}>
                      <button
                        type="button"
                        onClick={() => removeAP(idx)}
                        style={{
                          position: 'absolute', top: 10, right: 12,
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--gray-300)', fontSize: 16, lineHeight: 1,
                          padding: 2, borderRadius: 4, transition: 'color .15s'
                        }}
                        onMouseEnter={e => e.target.style.color = 'var(--danger)'}
                        onMouseLeave={e => e.target.style.color = 'var(--gray-300)'}
                        title="Ansprechpartner entfernen"
                      >✕</button>

                      <div style={{ fontSize: 11, fontWeight: 600, color: ap._pending ? 'var(--primary)' : 'var(--gray-500)', marginBottom: 10 }}>
                        {ap._pending ? 'Aus Datenbank verknüpft' : `Ansprechpartner ${idx + 1}`}
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label">Name *</label>
                          <input className="form-input" value={ap.name} onChange={e => updateAP(idx, 'name', e.target.value)} placeholder="Vor- und Nachname" />
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label">Funktion / Position</label>
                          <input className="form-input" value={ap.funktion || ''} onChange={e => updateAP(idx, 'funktion', e.target.value)} placeholder="z.B. Serviceberater" />
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.5fr', gap: 10 }}>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label">Telefon</label>
                          <input className="form-input" type="tel" value={ap.telefon || ''} onChange={e => updateAP(idx, 'telefon', e.target.value)} />
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label">Mobil</label>
                          <input className="form-input" type="tel" value={ap.mobil || ''} onChange={e => updateAP(idx, 'mobil', e.target.value)} />
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label">E-Mail</label>
                          <input className="form-input" type="email" value={ap.email || ''} onChange={e => updateAP(idx, 'email', e.target.value)} />
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={() => setShowPicker(true)}
                    style={{
                      border: '2px dashed var(--gray-200)', borderRadius: 10,
                      background: 'none', cursor: 'pointer',
                      padding: '10px', fontSize: 13, color: 'var(--gray-400)',
                      transition: 'all .15s', width: '100%'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.color = 'var(--primary)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--gray-200)'; e.currentTarget.style.color = 'var(--gray-400)'; }}
                  >
                    + Weiteren Ansprechpartner hinzufügen
                  </button>
                </div>
              )}
            </div>

            {/* ── Actions ── */}
            <div style={{ padding: '12px 24px 20px', borderTop: '1px solid var(--gray-100)' }}>
              <div className="form-actions" style={{ margin: 0 }}>
                <button type="button" className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Speichern…' : 'Speichern'}
                </button>
              </div>
            </div>

          </form>
        </div>
      </div>

      {showPicker && (
        <PersonPicker
          onSelect={handlePersonSelected}
          onClose={() => setShowPicker(false)}
          excludeIds={excludeIds}
          kontaktId={initial?.id}
        />
      )}
    </div>
  );
}
